import type { TeachingMode, VerificationStatus, CaseStatus } from './enums';

// ─────────────────────────────────────────────
// Education
// ─────────────────────────────────────────────
// All education level definitions are now centralized in educationLevels.ts
// These exports re-expose them for backward compatibility

export {
  EDUCATION_LEVEL_DEFINITIONS,
  EDUCATION_SYSTEM_DEFINITIONS,
  EDUCATION_SYSTEM_OPTIONS,
  EDUCATION_SYSTEM_SHORT_LABELS,
  EDUCATION_LEVEL_VALUES,
  EDUCATION_LEVEL_LABELS,
  LEVEL_SUBJECTS,
  DASHBOARD_EDUCATION_LABELS,
  DASHBOARD_EDUCATION_COLORS,
  EDUCATION_LEVEL_OPTIONS as EDUCATION_LEVELS,
  getEducationSystemDefinition,
  getEducationLevelsForSystem,
  getEducationSystemLabel,
  getEducationSystemShortLabel,
  getSystemForLevel,
  getEducationLevelDefinition,
  getSubjectOptionsForLevel,
  getSubjectLabel,
  getSubjectsForLevel,
  getLevelLabel,
  getLevelGroupKey,
  formatEducationLevel,
  formatEducationSystemLevel,
  normalizeEducationLevel,
  LEVEL_GROUP_METADATA,
  LEVEL_GROUP_ORDER,
  isValidEducationSystem,
  isValidEducationLevel,
  isValidLevelForSystem,
  isValidSubjectForLevel,
  getLevelDashboardColor,
  isLegacyLevel,
  isNewGranularLevel,
  type EducationLevelDefinition,
  type EducationSystemDefinition,
  type EducationSystemId,
  type EducationLevelId,
  type SubjectId,
  type SubjectDefinition,
  type LevelDisplayOptions,
  type LevelGroupMetadata,
} from './educationLevels';

// ─────────────────────────────────────────────
// Teaching Mode
// ─────────────────────────────────────────────

export const TEACHING_MODE_LABELS: Record<TeachingMode, string> = {
  online: 'Online',
  physical: 'In-person',
  both: 'Online + In-person',
};

// ─────────────────────────────────────────────
// Verification Status
// ─────────────────────────────────────────────

export const VERIFICATION_STATUS_LABELS: Record<VerificationStatus, string> = {
  not_submitted: 'Not Submitted',
  pending: 'Pending',
  approved: 'Approved',
  rejected: 'Rejected',
};

export const VERIFICATION_STATUS_OPTIONS: Array<{
  label: string;
  value: VerificationStatus | 'all';
}> = [
  { label: 'All', value: 'all' },
  { label: 'Not Submitted', value: 'not_submitted' },
  { label: 'Pending', value: 'pending' },
  { label: 'Approved', value: 'approved' },
  { label: 'Rejected', value: 'rejected' },
];

// ─────────────────────────────────────────────
// Case Status
// ─────────────────────────────────────────────

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  pending_review: 'Pending Review',
  new: 'New',
  pending: 'Pending',
  action_needed: 'Action Needed',
  awaiting_payment: 'Awaiting Payment',
  rejected: 'Rejected',
  closed: 'Closed',
};

/** Full case status config (ordered by workflow) — used for Kanban + status badges */
export const CASE_STATUS_CONFIG: Array<{
  status: CaseStatus;
  label: string;
  color: string;
}> = [
  { status: 'pending_review', label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
  { status: 'new', label: 'New', color: 'bg-blue-100 text-blue-800' },
  { status: 'pending', label: 'Pending', color: 'bg-yellow-100 text-yellow-800' },
  { status: 'action_needed', label: 'Action Needed', color: 'bg-orange-100 text-orange-800' },
  { status: 'awaiting_payment', label: 'Awaiting Payment', color: 'bg-purple-100 text-purple-800' },
  { status: 'rejected', label: 'Rejected', color: 'bg-red-100 text-red-800' },
  { status: 'closed', label: 'Closed', color: 'bg-gray-100 text-gray-800' },
];

/** Case statuses allowed by PATCH /admin/cases/:caseId/status */
export const ADMIN_CASE_STATUS_UPDATE_VALUES = [
  'new',
  'pending',
  'action_needed',
  'awaiting_payment',
  'closed',
] as const;

export type AdminCaseStatusUpdate = (typeof ADMIN_CASE_STATUS_UPDATE_VALUES)[number];

export const ADMIN_CASE_STATUS_UPDATE_OPTIONS = CASE_STATUS_CONFIG.filter(
  (statusConfig): statusConfig is (typeof CASE_STATUS_CONFIG)[number] & {
    status: AdminCaseStatusUpdate;
  } => ADMIN_CASE_STATUS_UPDATE_VALUES.includes(statusConfig.status as AdminCaseStatusUpdate)
);

export const DEFAULT_VISIBLE_STATUSES: CaseStatus[] = [
  'pending_review',
  'new',
  'pending',
  'action_needed',
  'awaiting_payment',
];

export const PARENT_CASE_TABS = [
  'all',
  'underReview',
  'matching',
  'inProgress',
  'rejected',
] as const;

export type ParentCaseTab = (typeof PARENT_CASE_TABS)[number];

export type ParentCaseFilterStatus = Exclude<CaseStatus, 'closed'>;

// ─────────────────────────────────────────────
// Malaysia — Geography
// ─────────────────────────────────────────────

export const MALAYSIAN_STATES = [
  'Johor',
  'Kedah',
  'Kelantan',
  'Kuala Lumpur',
  'Labuan',
  'Malacca',
  'Negeri Sembilan',
  'Pahang',
  'Penang',
  'Perak',
  'Perlis',
  'Putrajaya',
  'Sabah',
  'Sarawak',
  'Selangor',
  'Terengganu',
];

export const AREAS_BY_STATE: Record<string, string[]> = {
  Kuala_Lumpur: ['Bukit Bintang', 'Cheras', 'Sentosa', 'Petaling Jaya', 'Ampang'],
  Selangor: ['Petaling Jaya', 'Subang Jaya', 'Shah Alam', 'Klang', 'Kajang'],
  Penang: ['George Town', 'Butterworth', 'Bayan Lepas', 'Bukit Mertajam'],
};

export const MALAYSIAN_DISTRICTS = [
  // Johor
  'Batu Pahat', 'Johor Bahru', 'Kluang', 'Kota Tinggi', 'Kulai', 'Mersing',
  'Muar', 'Pontian', 'Segamat', 'Tangkak',
  // Kedah
  'Baling', 'Bandar Baharu', 'Kota Setar', 'Kuala Muda', 'Kubang Pasu', 'Kulim',
  'Langkawi', 'Padang Terap', 'Pendang', 'Pokok Sena', 'Sik', 'Yan',
  // Kelantan
  'Bachok', 'Gua Musang', 'Jeli', 'Kota Bharu', 'Kuala Krai', 'Machang',
  'Pasir Mas', 'Pasir Puteh', 'Tanah Merah', 'Tumpat',
  // Kuala Lumpur
  'Kuala Lumpur',
  // Labuan
  'Labuan',
  // Malacca
  'Alor Gajah', 'Jasin', 'Melaka Tengah',
  // Negeri Sembilan
  'Jelebu', 'Jempol', 'Kuala Pilah', 'Port Dickson', 'Rembau', 'Seremban', 'Tampin',
  // Pahang
  'Bentong', 'Bera', 'Cameron Highlands', 'Jerantut', 'Kuantan', 'Lipis',
  'Maran', 'Pekan', 'Raub', 'Rompin', 'Temerloh',
  // Penang
  'Barat Daya', 'Seberang Perai Selatan', 'Seberang Perai Tengah',
  'Seberang Perai Utara', 'Timur Laut',
  // Perak
  'Bagan Datuk', 'Batang Padang', 'Hilir Perak', 'Hulu Perak', 'Kampar',
  'Kerian', 'Kinta', 'Kuala Kangsar', 'Larut, Matang dan Selama', 'Manjung',
  'Muallim', 'Perak Tengah',
  // Perlis
  'Perlis',
  // Putrajaya
  'Putrajaya',
  // Sabah
  'Beaufort', 'Beluran', 'Keningau', 'Kota Belud', 'Kota Kinabalu', 'Kota Marudu',
  'Kuala Penyu', 'Kudat', 'Kunak', 'Lahad Datu', 'Nabawan', 'Papar', 'Penampang',
  'Pitas', 'Ranau', 'Sandakan', 'Semporna', 'Sipitang', 'Tambunan', 'Tawau',
  'Telupid', 'Tenom', 'Tongod', 'Tuaran',
  // Sarawak
  'Asajaya', 'Betong', 'Bintulu', 'Dalat', 'Daro', 'Julau', 'Kabong', 'Kanowit',
  'Kapit', 'Kuching', 'Lawas', 'Limbang', 'Lubok Antu', 'Lundu', 'Maradong',
  'Marudi', 'Matu', 'Miri', 'Mukah', 'Pakan', 'Pusa', 'Samarahan', 'Sarikei',
  'Saratok', 'Sebauh', 'Selangau', 'Serian', 'Sibu', 'Simunjan', 'Song', 'Sri Aman',
  'Subis', 'Tatau', 'Tanjung Manis',
  // Selangor
  'Gombak', 'Hulu Langat', 'Hulu Selangor', 'Klang', 'Kuala Langat',
  'Kuala Selangor', 'Petaling', 'Sabak Bernam', 'Sepang',
  // Terengganu
  'Besut', 'Dungun', 'Hulu Terengganu', 'Kemaman', 'Kuala Nerus',
  'Kuala Terengganu', 'Marang', 'Setiu',
];

// ─────────────────────────────────────────────
// Personal
// ─────────────────────────────────────────────

export const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

// ─────────────────────────────────────────────
// Academic Qualifications
// ─────────────────────────────────────────────

export const DEGREE_TYPES = [
  'SPM',
  'STPM',
  'A-Level',
  'Foundation',
  'Diploma',
  'Associate Degree',
  "Bachelor's Degree",
  "Master's Degree",
  'PhD',
  'Professional Certificate',
  'Other',
];

export const INSTITUTIONS = [
  // Malaysian Public Universities
  'Universiti Malaya (UM)',
  'Universiti Kebangsaan Malaysia (UKM)',
  'Universiti Putra Malaysia (UPM)',
  'Universiti Sains Malaysia (USM)',
  'Universiti Teknologi Malaysia (UTM)',
  'Universiti Teknologi MARA (UiTM)',
  'Universiti Utara Malaysia (UUM)',
  'Universiti Malaysia Perlis (UniMAP)',
  'Universiti Malaysia Kelantan (UMK)',
  'Universiti Malaysia Terengganu (UMT)',
  'Universiti Teknikal Malaysia Melaka (UTeM)',
  'Universiti Tun Hussein Onn Malaysia (UTHM)',
  'Universiti Malaysia Pahang (UMP)',
  'Universiti Malaysia Sabah (UMS)',
  'Universiti Malaysia Sarawak (UNIMAS)',
  'Universiti Pendidikan Sultan Idris (UPSI)',
  'Universiti Sains Islam Malaysia (USIM)',
  'Universiti Sultan Zainal Abidin (UniSZA)',
  'Universiti Pertahanan Nasional Malaysia (UPNM)',
  // Malaysian Private Universities
  'Monash University Malaysia',
  'University of Nottingham Malaysia',
  'Curtin University Malaysia',
  'Swinburne University of Technology Sarawak',
  'Heriot-Watt University Malaysia',
  'University of Southampton Malaysia',
  'Newcastle University Medicine Malaysia',
  'Multimedia University (MMU)',
  'Universiti Tenaga Nasional (UNITEN)',
  'Universiti Tunku Abdul Rahman (UTAR)',
  'UCSI University',
  "Taylor's University",
  'Sunway University',
  'Asia Pacific University (APU)',
  'HELP University',
  'International Medical University (IMU)',
  'Infrastructure University Kuala Lumpur (IUKL)',
  'Limkokwing University',
  'Management and Science University (MSU)',
  'Nilai University',
  'Perdana University',
  'SEGi University',
  'Tunku Abdul Rahman University of Management and Technology (TAR UMT)',
  'INTI International University',
  'KDU University College',
  'Raffles University',
  'Binary University',
  // International Universities
  'Harvard University',
  'Stanford University',
  'Massachusetts Institute of Technology (MIT)',
  'University of Oxford',
  'University of Cambridge',
  'Imperial College London',
  'University of California, Berkeley',
  'University of California, Los Angeles (UCLA)',
  'Yale University',
  'Princeton University',
  'Columbia University',
  'University of Chicago',
  'Cornell University',
  'University of Pennsylvania',
  'Johns Hopkins University',
  'Northwestern University',
  'Duke University',
  'University of Michigan',
  'University of Toronto',
  'University of British Columbia',
  'McGill University',
  'National University of Singapore (NUS)',
  'Nanyang Technological University (NTU)',
  'Tsinghua University',
  'Peking University',
  'University of Hong Kong',
  'Chinese University of Hong Kong',
  'University of Melbourne',
  'Australian National University',
  'University of Sydney',
  'University of Queensland',
  'University of Auckland',
  'Seoul National University',
  'Korea University',
  'Yonsei University',
  'University of Tokyo',
  'Kyoto University',
  'Other',
];

// ─────────────────────────────────────────────
// Tutor Experience
// ─────────────────────────────────────────────

export const TUITION_EXPERIENCE_YEARS = [
  { value: 0, label: 'Less than 1 year' },
  { value: 1, label: '1 year' },
  { value: 2, label: '2 years' },
  { value: 3, label: '3-5 years' },
  { value: 4, label: '6-10 years' },
  { value: 5, label: '11-15 years' },
  { value: 6, label: '16-20 years' },
  { value: 7, label: '20+ years' },
] as const;

// ─────────────────────────────────────────────
// Scheduling
// ─────────────────────────────────────────────

export const DAYS_OF_WEEK = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
];

export const TIME_SLOT_CONFIGS: Record<string, { label: string; startHour: number; endHour: number }> = {
  Morning: { label: 'Morning', startHour: 8, endHour: 11 },
  Afternoon: { label: 'Afternoon', startHour: 12, endHour: 16 },
  Evening: { label: 'Evening', startHour: 17, endHour: 20 },
};

export const HOUR_RANGES: Record<string, { min: number; max: number }> = {
  Morning: { min: 6, max: 11 },
  Afternoon: { min: 12, max: 16 },
  Evening: { min: 17, max: 23 },
};
